import { ConfirmationDialog, useDisclosure } from "@hightouch/htui";
import capitalize from "lodash/capitalize";
import {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

import { useNavigate, useSearchParams, useLocation } from "src/router";

import { DraftSubmissionModal } from "src/components/modals/draft-submission-modal";
import { useUser } from "src/contexts/user-context";
import {
  DraftOperation,
  type DraftsQuery,
  type ModelDraft,
  type SyncDraft,
} from "src/graphql";
import { useDraftMerger } from "src/hooks/use-draft-merger";

type UpdateResourceOrDraftFn = (
  updatePayload: SyncDraft | ModelDraft,
  onUpdate: () => void,
  update: UpdateResourceFunc,
  resourceIsDraft: boolean,
) => Promise<void>;

type DraftProviderType = {
  draft: DraftsQuery["drafts"][0] | undefined;
  isFetchingDraft: boolean;
  draftChange: SyncDraft | ModelDraft | undefined;
  setDraftChange: (change: SyncDraft | ModelDraft) => void;
  editingDraft: boolean;
  editingDraftChanges: boolean;
  setEditingDraft: (editing: boolean) => void;
  setSubmitDraftModalOpen: (open: boolean) => void;
  resourceType: "model" | "sync" | undefined;
  onViewDraft: () => void;
  updateResourceOrDraft: UpdateResourceOrDraftFn | undefined;
  mergeResourceWithDraft: (
    resource: Record<string, unknown>,
  ) => Record<string, unknown>;
};

const defaultDraftContext: DraftProviderType = {
  draft: undefined,
  isFetchingDraft: false,
  draftChange: undefined,
  setDraftChange: () => undefined,
  editingDraft: false,
  editingDraftChanges: false,
  setEditingDraft: () => undefined,
  setSubmitDraftModalOpen: () => undefined,
  resourceType: undefined,
  onViewDraft: () => undefined,
  updateResourceOrDraft: undefined,
  mergeResourceWithDraft: (resource) => resource,
};

export const DraftContext =
  createContext<DraftProviderType>(defaultDraftContext);
DraftContext.displayName = "DraftContext";

type UpdateResourceFunc = () => Promise<unknown>;

interface Props {
  children: ReactNode;
  initialResourceIsDraft: boolean;
  resourceType: "model" | "sync";
  resourceId: string | undefined;
  invalidateQueries?: boolean;
  onPublish?: () => void;
}

export const DraftProvider: FC<Props> = ({
  children,
  resourceType,
  resourceId,
  initialResourceIsDraft,
  invalidateQueries = false,
  onPublish,
}) => {
  const [draftChange, setDraftChange] = useState<SyncDraft | ModelDraft>();
  const [submitDraftModalOpen, setSubmitDraftModalOpen] = useState(false);

  const {
    isOpen: existingChangesModalOpen,
    onOpen: openExistingChangesModal,
    onClose: closeExistingChangesModal,
  } = useDisclosure();
  const [handleUpdate, setHandleUpdate] = useState<() => void>();
  const navigate = useNavigate();
  const { workspace } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const editingDraft = searchParams.get("editing") === "true";
  const location = useLocation();

  // Ref keeps `setEditingDraft` referentially stable across renders without
  // sacrificing the original "skip setSearchParams when the param isn't set"
  // short-circuit. Reading the latest `searchParams` from a ref preserves
  // strict parity with the pre-perf-fix behavior.
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const {
    draft,
    isFetching: isFetchingDraft,
    mergeResourceWithDraft,
  } = useDraftMerger({
    resourceId: resourceId?.toString() ?? "",
    resourceType,
  });

  const setEditingDraft = useCallback(
    (editing: boolean) => {
      if (editing) {
        setSearchParams(
          (prevParams) => {
            prevParams.set("editing", "true");
            return prevParams;
          },
          { replace: true, state: location.state },
        );
      } else if (searchParamsRef.current.has("editing")) {
        setSearchParams(
          (prevParams) => {
            prevParams.delete("editing");
            return prevParams;
          },
          { replace: true, state: location.state },
        );
      }
    },
    [setSearchParams, location.state],
  );

  const updateResourceOrDraft = useCallback(
    async (
      updatePayload: SyncDraft | ModelDraft,
      onUpdate: () => void,
      update: UpdateResourceFunc,
      resourceIsDraft: boolean,
    ) => {
      if (!resourceId) {
        return;
      }

      if (!workspace?.approvals_required || resourceIsDraft) {
        await update();
        onUpdate();
        setEditingDraft(false);
        return;
      }

      // only set the draft change if we're currently editing - otherwise, we're going to show
      // the modal confirmation to delete the existing draft.
      if (draft && !editingDraft) {
        openExistingChangesModal();
        return;
      }

      setEditingDraft(true);
      setHandleUpdate(() => onUpdate);
      setSubmitDraftModalOpen(true);
      setDraftChange(updatePayload);
    },
    [
      resourceId,
      workspace?.approvals_required,
      draft,
      editingDraft,
      openExistingChangesModal,
      setEditingDraft,
    ],
  );

  const onViewDraft = useCallback(() => {
    // this can't happen - we can't view a draft with no resource id set.
    if (!resourceId) {
      return;
    }

    if (draft?.approval_requests.length === 0) {
      setSubmitDraftModalOpen(true);
    } else {
      navigate(makeViewDraftUrl(resourceType, resourceId));
    }
  }, [resourceId, draft?.approval_requests.length, navigate, resourceType]);

  const customMergeResourceWithDraft = useCallback(
    (resource: Readonly<Record<string, unknown>>) => {
      if (!editingDraft) {
        return resource;
      }

      return mergeResourceWithDraft(resource);
    },
    [editingDraft, mergeResourceWithDraft],
  );

  const editingDraftChanges =
    editingDraft && draft?.operation === DraftOperation.Update;

  const value = useMemo(
    () => ({
      draft,
      isFetchingDraft,
      setDraftChange,
      editingDraft,
      editingDraftChanges,
      setEditingDraft,
      setSubmitDraftModalOpen,
      resourceType,
      draftChange,
      onViewDraft,
      updateResourceOrDraft,
      mergeResourceWithDraft: customMergeResourceWithDraft,
    }),
    [
      draft,
      isFetchingDraft,
      editingDraft,
      editingDraftChanges,
      setEditingDraft,
      resourceType,
      draftChange,
      onViewDraft,
      updateResourceOrDraft,
      customMergeResourceWithDraft,
    ],
  );

  return (
    <DraftContext.Provider value={value}>
      <>
        {children}
        {workspace?.approvals_required && (
          <ConfirmationDialog
            onClose={closeExistingChangesModal}
            onConfirm={() => setEditingDraft(true)}
            variant="warning"
            title={`${capitalize(resourceType)} has existing draft changes`}
            isOpen={existingChangesModalOpen}
            confirmButtonText="View existing changes"
          >
            This {resourceType} has existing draft changes, go to the draft to
            view or discard them before making any further changes.
          </ConfirmationDialog>
        )}
        {workspace?.approvals_required && (
          <DraftSubmissionModal
            approverOptions={
              resourceType === "model"
                ? { type: "model", modelId: resourceId ?? "" }
                : { type: "sync", syncId: resourceId ?? "" }
            }
            permission={
              resourceType === "model"
                ? {
                    v2: {
                      resource: "model",
                      grant: "can_approve",
                      id: resourceId ?? "",
                    },
                  }
                : {
                    v2: {
                      resource: "sync",
                      grant: "can_approve",
                      id: resourceId ?? "",
                    },
                  }
            }
            draft={draftChange}
            getResourceId={() => (resourceId ? String(resourceId) : "")}
            invalidateQueriesOnSubmit={invalidateQueries}
            open={submitDraftModalOpen}
            operation={
              initialResourceIsDraft
                ? DraftOperation.Create
                : DraftOperation.Update
            }
            resource={resourceType}
            onClose={() => {
              setSubmitDraftModalOpen(false);
            }}
            onSubmit={(
              _resourceId: string,
              { publishNow }: { publishNow: boolean },
            ) => {
              if (publishNow) {
                if (handleUpdate) {
                  handleUpdate();
                }

                if (onPublish) {
                  onPublish();
                }
              }
            }}
          />
        )}
      </>
    </DraftContext.Provider>
  );
};

const makeViewDraftUrl = (resourceType: "model" | "sync", resourceId: string) =>
  `/${resourceType === "model" ? "models" : "syncs"}/${resourceId}/draft`;

export const useDraft = () => useContext(DraftContext);
